import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SpinnerService } from './shared/services/spinner.service';
import { filter, take } from 'rxjs';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
  InteractionType,
  PopupRequest,
  RedirectRequest,
} from '@azure/msal-browser';
import { b2cPolicies } from './auth-config';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Claim } from './shared/models/dynamic.model';
import { MessagePopupComponent } from './shared/components/message-popup/message-popup.component';
import { popupWidth } from './shared/utility/constants';
import { ModalService } from './shared/services/modal.service';
import { AuthService } from './shared/services/auth.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  public isForPrintPreview = false;
  public isSpinnerLoad = true;
  dataSource: Claim[] = [];
  private spinnerTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private readonly spinnerService: SpinnerService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly msalService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly destroyRef: DestroyRef,
    private readonly modalService: ModalService,
    private readonly authService: AuthService,
    private readonly router: Router
  ) {}

  ngOnInit() {
    this.destroyRef.onDestroy(() => {
      if (this.spinnerTimer) {
        clearTimeout(this.spinnerTimer);
      }
    });
    this.spinnerStatus();
    this.msalService
      .handleRedirectObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
    this.b2cAuthorizeRequest();
    this.checkLayoutRequired();
    this.schedulePlainStorageMigrationAfterNavigation();
  }

  private schedulePlainStorageMigrationAfterNavigation(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        queueMicrotask(() => this.authService.migratePlainStorageKeys());
      });
  }

  private b2cAuthorizeRequest() {
    if (location.href.includes('login=')) {
      this.msalService.initialize().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.authService.login());
    }

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            (msg.eventType === EventType.LOGIN_SUCCESS ||
              msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
              msg.eventType === EventType.SSO_SILENT_SUCCESS) &&
            msg.interactionType !== InteractionType.Silent
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result: EventMessage) => {
        let payload = result.payload as AuthenticationResult;
        this.getClaims(payload.idTokenClaims);

        if (
          this.dataSource.find(x => x.claim == 'tfp')?.value ===
          b2cPolicies.names.resetPassword
        ) {
          const activeAccount =
            this.msalService.instance.getActiveAccount() ??
            this.msalService.instance.getAllAccounts()[0];
          const dialogRef = this.modalService.openPopup(
            {
              isFooterRequired: true,
              isForWarning: true,
              message: popupWidth.windowAlert,
            },
            '',
            MessagePopupComponent,
            popupWidth.warningPopup
          );
          dialogRef.afterClosed().pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(data => {
            if (data === true) {
              this.msalService.logoutRedirect({
                account: activeAccount,
              });
            }
          });
          this.spinnerService.hideSpinner();
        } else if (payload) {
          this.authService.getTokenWithIp(payload.accessToken).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        }
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_FAILURE ||
            msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result: EventMessage) => {
        // Checking for the forgot password error. Learn more about B2C error codes at
        // https://learn.microsoft.com/azure/active-directory-b2c/error-codes
        if (result.error && result.error.message.indexOf('AADB2C90118') > -1) {
          let resetPasswordFlowRequest: RedirectRequest | PopupRequest = {
            authority: b2cPolicies.authorities.resetPassword.authority,
            scopes: ['openid', 'offline_access'],
          };

          this.login(resetPasswordFlowRequest);
          this.spinnerService.hideSpinner();
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
        this.authService.tryInitLocalStorageEncryptionKey();
        this.authService.migratePlainStorageKeys();
        this.getClaims(
          this.msalService.instance.getActiveAccount()?.idTokenClaims
        );
      });
  }

  private checkAndSetActiveAccount() {
    let activeAccount = this.msalService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.msalService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  private login(userFlowRequest?: RedirectRequest | PopupRequest) {
    this.authService.login(userFlowRequest);
  }

  private getClaims(claims: any) {
    let list: Claim[] = new Array<Claim>();
    if (claims) {
      Object.keys(claims).forEach(function (k, v) {
        let c = new Claim();
        c.id = v;
        c.claim = k;
        c.value = claims ? claims[k] : null;
        list.push(c);
      });
      this.dataSource = list;
    }
  }

  private spinnerStatus() {
    this.spinnerService.visibility
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        if (this.spinnerTimer) {
          clearTimeout(this.spinnerTimer);
        }
        this.spinnerTimer = setTimeout(() => {
          this.spinnerTimer = undefined;
          this.isSpinnerLoad = data;
          this.changeDetectorRef.detectChanges();
        });
      });
  }

  private checkLayoutRequired() {
    if (window.location.pathname === '/airway-bill-print-preview') {
      this.isForPrintPreview = true;
    } else {
      this.isForPrintPreview = false;
    }
  }
}
